import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';

/** Must match V2 API `status` after backend map. */
export const SHIPMENT_DISPLAY_STATUS = {
  Draft: 'Draft',
  Booked: 'Booked',
  Active: 'Active',
  Late: 'Late',
  Delivered: 'Delivered',
  Closed: 'Closed',
} as const;

export type ShipmentDisplayStatus =
  (typeof SHIPMENT_DISPLAY_STATUS)[keyof typeof SHIPMENT_DISPLAY_STATUS];

/** Sidebar / query param filter keys. */
export const SHIPMENT_QUEUE_FILTER = {
  all: 'all',
  ...SHIPMENT_DISPLAY_STATUS,
  draft: 'draft',
} as const;

export type ShipmentQueueKey =
  (typeof SHIPMENT_QUEUE_FILTER)[keyof typeof SHIPMENT_QUEUE_FILTER];

export const STATUS_BADGE_CLASS: Record<ShipmentDisplayStatus, string> = {
  Draft: 'bg-gray-100 text-gray-600',
  Booked: 'bg-purple-100 text-purple-700',
  Active: 'bg-blue-100 text-blue-700',
  Late: 'bg-red-100 text-red-700',
  Delivered: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500',
};

export const STATUS_MAP_HEX: Record<ShipmentDisplayStatus, string> = {
  Draft: '#9CA3AF',
  Booked: '#8B5CF6',
  Active: '#3B82F6',
  Late: '#EF4444',
  Delivered: '#10B981',
  Closed: '#6B7280',
};

/** @deprecated Use SHIPMENT_DISPLAY_STATUS.Late */
export const LATE_BADGE_LABEL = SHIPMENT_DISPLAY_STATUS.Late;

/** @deprecated Use STATUS_MAP_HEX.Late */
export const LATE_MAP_HEX = STATUS_MAP_HEX.Late;

export type ShipmentMapFilterKey = ShipmentDisplayStatus;

export type ShipmentStatusUiInput = Pick<
  V2ShipmentDetail,
  'status' | 'archived_at' | 'ended_at' | 'end_at'
>;

export interface ShipmentMetrics {
  total: number;
  Booked: number;
  Active: number;
  Late: number;
  Delivered: number;
  draft: number;
  Closed: number;
  picked_up_today: number;
  delivery_today: number;
  carriers: string[];
}

export type ShipmentListRow = {
  id: number;
  status?: string;
  archived_at?: string | null;
  ended_at?: string | null;
  end_at?: string | null;
  user_id?: number | null;
  exception_flags?: number;
  [key: string]: unknown;
};

function parseInstant(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return instant;
}

function isPastScheduledEnd(
  endAt: string | null | undefined,
  now: Date,
): boolean {
  if (endAt == null || endAt === '') return false;
  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return false;
  return now.getTime() > end.getTime();
}

export function isShipmentLate(
  shipment: ShipmentStatusUiInput,
  now: Date = new Date(),
): boolean {
  if (shipment.status === SHIPMENT_DISPLAY_STATUS.Late) return true;
  if (shipment.archived_at != null && shipment.archived_at !== '') return false;
  if (shipment.ended_at != null && shipment.ended_at !== '') return false;
  if (shipment.status !== SHIPMENT_DISPLAY_STATUS.Active) return false;
  return isPastScheduledEnd(shipment.end_at, now);
}

/** Delivered after scheduled `end_at` — secondary popup badge only, not pin color. */
export function isShipmentDeliveredLate(
  shipment: ShipmentStatusUiInput,
): boolean {
  if (shipment.archived_at != null && shipment.archived_at !== '') return false;
  if (shipment.status !== SHIPMENT_DISPLAY_STATUS.Delivered) return false;
  const endedAt = parseInstant(shipment.ended_at);
  const scheduledEnd = parseInstant(shipment.end_at);
  if (!endedAt || !scheduledEnd) return false;
  return endedAt.getTime() > scheduledEnd.getTime();
}

/** Secondary "Late" badge in map popup for delivered late only. */
export function showLateBadgeInMapPopup(
  shipment: ShipmentStatusUiInput,
): boolean {
  return isShipmentDeliveredLate(shipment);
}

export function mapColorHex(
  shipment: ShipmentStatusUiInput,
  now: Date = new Date(),
): string {
  if (isShipmentLate(shipment, now)) return STATUS_MAP_HEX.Late;
  const status = shipment.status as ShipmentDisplayStatus | undefined;
  if (status && status in STATUS_MAP_HEX) {
    return STATUS_MAP_HEX[status];
  }
  return STATUS_MAP_HEX.Draft;
}

export function mapFilterKey(
  shipment: ShipmentStatusUiInput,
  now: Date = new Date(),
): ShipmentMapFilterKey {
  if (isShipmentLate(shipment, now)) return SHIPMENT_DISPLAY_STATUS.Late;
  const status = shipment.status as ShipmentDisplayStatus | undefined;
  if (status && status in STATUS_MAP_HEX) {
    return status;
  }
  return SHIPMENT_DISPLAY_STATUS.Draft;
}

export type ShipmentListMapLegendEntry = {
  statusKey: ShipmentMapFilterKey;
  label: string;
  color: string;
};

export const SHIPMENT_LIST_MAP_LEGEND_ENTRIES: ShipmentListMapLegendEntry[] = [
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Booked,
    label: SHIPMENT_DISPLAY_STATUS.Booked,
    color: STATUS_MAP_HEX.Booked,
  },
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Active,
    label: SHIPMENT_DISPLAY_STATUS.Active,
    color: STATUS_MAP_HEX.Active,
  },
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Late,
    label: SHIPMENT_DISPLAY_STATUS.Late,
    color: STATUS_MAP_HEX.Late,
  },
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Delivered,
    label: SHIPMENT_DISPLAY_STATUS.Delivered,
    color: STATUS_MAP_HEX.Delivered,
  },
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Closed,
    label: SHIPMENT_DISPLAY_STATUS.Closed,
    color: STATUS_MAP_HEX.Closed,
  },
  {
    statusKey: SHIPMENT_DISPLAY_STATUS.Draft,
    label: SHIPMENT_DISPLAY_STATUS.Draft,
    color: STATUS_MAP_HEX.Draft,
  },
];

const STATUS_SLUG: Record<ShipmentMapFilterKey, string> = {
  [SHIPMENT_DISPLAY_STATUS.Draft]: 'draft',
  [SHIPMENT_DISPLAY_STATUS.Booked]: 'booked',
  [SHIPMENT_DISPLAY_STATUS.Active]: 'active',
  [SHIPMENT_DISPLAY_STATUS.Late]: 'late',
  [SHIPMENT_DISPLAY_STATUS.Delivered]: 'delivered',
  [SHIPMENT_DISPLAY_STATUS.Closed]: 'closed',
};

export function mapBadgeCssClass(
  statusKey: ShipmentMapFilterKey,
): string {
  return `gpx-shipment-map-badge gpx-shipment-map-badge--${STATUS_SLUG[statusKey]}`;
}

export function mapClusterDotCssClass(statusKey: ShipmentMapFilterKey): string {
  return `gpx-shipment-map-tooltip__cluster-dot gpx-shipment-map-cluster-dot--${STATUS_SLUG[statusKey]}`;
}

export function displayStatusForRow(row: ShipmentListRow): ShipmentDisplayStatus {
  const status = row.status;
  if (status && status in STATUS_BADGE_CLASS) {
    return status as ShipmentDisplayStatus;
  }
  return SHIPMENT_DISPLAY_STATUS.Draft;
}

export function isShipmentArchived(row: Pick<ShipmentListRow, 'archived_at'>): boolean {
  return row.archived_at != null && row.archived_at !== '';
}
